// eslint-disable-next-line no-restricted-imports
import { useTheme } from '@emotion/react';
// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import { Typography } from '@iheartjane/reefer';
import { spacing } from '@iheartjane/reefer-emotion';
import type { PropsWithoutRef, ReactNode } from 'react';
import type { FieldMetaState } from 'react-final-form';
import { Field } from 'react-final-form';

import { FieldControl } from './field-control';
import { HelperText } from './helper-text';
import { InputControl } from './input-control';
import { Label } from './label';
import { useTextField } from './text-field-context';

export type Variant = 'material' | 'border';

type InputProps = {
  variant?: Variant;
};

const StyledBorderInput = styled.input<InputProps>({
  border: `1px solid var(--colors-grays-light)`,
  boxSizing: 'border-box',
  flex: '1 1 auto',
  ...spacing({ px: 8, my: 8 }),
  backgroundColor: 'var(--colors-grays-white)',
  color: 'var(--colors-text-main)',
  borderRadius: 4,
  height: 40,
  width: '100%',
  WebkitAppearance: 'none',
  '&::-ms-clear': {
    display: 'none',
  },
  '&::-webkit-search-cancel-button': {
    display: 'none',
  },
  '&:-webkit-autofill': {
    boxShadow: '0 0 0 100px white inset',
  },
  '::placeholder': {
    color: 'var(--colors-grays-mid)',
  },
  ':disabled': {
    color: 'var(--colors-grays-mid)',
    backgroundColor: 'var(--colors-grays-ultralight)',
  },
});

const StyledMaterialInput = styled.input<InputProps>({
  ...spacing({ py: 8, px: 0 }),
  width: '100%',
  display: 'block',
  outline: 'none',
  background: 'transparent',
  color: 'var(--colors-text-main)',
  border: '0',
  '&:disabled': {
    color: 'var(--colors-grays-light)',
  },
  '&::placeholder': {
    color: 'var(--colors-grays-mid)',
    opacity: 0,
    transition: 'opacity 200ms ease-in-out',
  },
  '&:focus::placeholder': {
    opacity: 1,
  },
  '&:-webkit-autofill': {
    WebkitBoxShadow: '0 0 0 100px white inset',
  },
});

const Input = ({
  ...props
}: PropsWithoutRef<JSX.IntrinsicElements['input']>) => {
  const { inputRef, variant } = useTextField();

  return (
    <InputControl disabled={props.disabled}>
      <Typography as="div" variant="body">
        {variant === 'material' ? (
          <StyledMaterialInput ref={inputRef} {...props} />
        ) : (
          <StyledBorderInput ref={inputRef} {...props} />
        )}
      </Typography>
    </InputControl>
  );
};

export interface TextInputProps extends PropsWithoutRef<
  JSX.IntrinsicElements['input']
> {
  error?: boolean;
  helperText?: ReactNode;
  id: string;
  isFocused?: boolean;
  label?: string;
  labelId?: string;
  success?: boolean;
  variant?: Variant;
}

export const TextInput = ({
  label,
  labelId,
  defaultValue,
  disabled,
  placeholder,
  helperText,
  variant,
  isFocused,
  error,
  success,
  name,
  id,
  ...props
}: TextInputProps) => {
  const theme = useTheme();
  return (
    <FieldControl
      isFocused={isFocused}
      variant={variant}
      error={error}
      success={success}
    >
      <Label id={labelId} htmlFor={id} disabled={disabled}>
        {label}
      </Label>
      <Input
        id={id}
        aria-describedby={helperText ? `${id}-helper` : undefined}
        aria-invalid={error ? 'true' : 'false'}
        defaultValue={defaultValue}
        disabled={disabled}
        placeholder={placeholder}
        name={name}
        // eslint-disable-next-line react/forbid-component-props -- legacy emotion
        css={
          error && variant === 'border'
            ? { border: `1px solid ${theme.colors.error}` }
            : {}
        }
        {...props}
      />
      {helperText && <HelperText id={`${id}-helper`}>{helperText}</HelperText>}
    </FieldControl>
  );
};

export interface TextInputFieldProps extends TextInputProps {
  name: string;
  renderSubmitError?: (meta: FieldMetaState<any>) => ReactNode;
}

export const TextInputField = (props: TextInputFieldProps) => (
  <Field type={props.type} label={props.label} name={props.name}>
    {({ meta, input }) => {
      const hasError = (meta.error || meta.submitError) && meta.touched;
      // Show error or helper text
      const renderError = () => {
        if (!hasError) return null;
        if (props.renderSubmitError && meta.submitError)
          return props.renderSubmitError(meta.submitError);
        return meta.error || meta.submitError;
      };
      const helperText = hasError ? renderError() : props.helperText;

      // Overwrite helperText and error props based on final form data.
      return (
        <TextInput
          {...props}
          {...input}
          helperText={helperText}
          error={hasError}
        />
      );
    }}
  </Field>
);
